import AreaChart from '@lyra/core/components/AreaChart'
import ChartTooltip from '@lyra/core/components/ChartTooltip'
import Section from '@lyra/core/components/Section'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatUSD from '@lyra/core/utils/formatUSD'
import useOrderbook from '@lyra/web/hooks/useOrderbook'
import useTicker from '@lyra/web/hooks/useTicker'
import { formatOrderPrice } from '@lyra/web/utils/order'
import { useMemo } from 'react'
import { StackProps } from 'tamagui'

type Props = {
  instrumentName: string
} & StackProps

type DataPoint = {
  price: number
  size: number
  volume: number
  bidSize?: number
  askSize?: number
}

const DepthChart = ({ instrumentName, ...stackProps }: Props) => {
  const orderbook = useOrderbook(instrumentName, 1)
  const ticker = useTicker(instrumentName)

  const { chartData, maxDomain } = useMemo(() => {
    if (!orderbook) {
      return { chartData: [], maxDomain: 'dataMax' }
    }

    const { bids, asks } = orderbook

    let cumulativeBidSize = 0
    const bidData: DataPoint[] = bids
      .map(([priceStr, amountStr]) => {
        const price = parseFloat(priceStr)
        const amount = parseFloat(amountStr)
        cumulativeBidSize += amount
        return {
          price,
          size: amount,
          volume: price * amount,
          bidSize: cumulativeBidSize,
          askSize: undefined,
        }
      })
      .reverse()

    let cumulativeAskSize = 0
    const askData: DataPoint[] = asks.map(([priceStr, amountStr]) => {
      const price = parseFloat(priceStr)
      const amount = parseFloat(amountStr)
      cumulativeAskSize += amount
      return {
        price,
        size: amount,
        volume: price * amount,
        askSize: cumulativeAskSize,
        bidSize: undefined,
      }
    })

    const chartData = [...bidData, ...askData]
    const maxDomain = Math.max(cumulativeAskSize, cumulativeBidSize) * 1.1

    return { chartData, maxDomain }
  }, [orderbook])

  if (!orderbook || !ticker) {
    return <Section.Spinner {...stackProps} />
  }

  const tickSize = +ticker.tick_size

  return (
    <AreaChart
      {...stackProps}
      flex={1}
      data={chartData}
      dataKeys={[
        {
          key: 'bidSize',
          color: 'green',
          type: 'step',
        },
        {
          key: 'askSize',
          color: 'red',
          type: 'step',
        },
      ]}
      yAxisProps={{
        tickFormatter: (v) => formatNumber(v, { dps: 2 }),
        tickLine: false,
        domain: ['dataMin', maxDomain],
        orientation: 'right',
      }}
      xAxisProps={{
        tickCount: 15,
        tickLine: false,
        tickFormatter: (v) => formatUSD(v, { dps: 0 }),
      }}
      xAxisDataKey="price"
      tooltipProps={{
        content: ({ active, payload }) => {
          if (active && payload && payload.length) {
            const payloadData = payload[0].payload
            const isBid = payload[0].dataKey === 'bidSize'
            return (
              <ChartTooltip
                xLabel={isBid ? 'Bids' : 'Asks'}
                legend={[
                  {
                    dataKey: 'Size',
                    value: formatNumber(payloadData.size),
                    color: 'white',
                  },
                  {
                    dataKey: 'Total Size',
                    value: formatNumber(isBid ? payloadData.bidSize : payloadData.askSize),
                    color: 'white',
                  },
                  {
                    dataKey: 'Price',
                    value: formatOrderPrice(payloadData.price, tickSize),
                    color: isBid ? 'green' : 'red',
                  },
                  {
                    dataKey: 'Volume',
                    value: formatUSD(payloadData.volume),
                    color: 'amber',
                  },
                ]}
              />
            )
          }
        },
      }}
    />
  )
}

export default DepthChart
